import AutoCompleteFormular from "../../../components/autocomplete/AutoCompleteFormular";
import {
  FirmaKlienterklaering,
  MakeOptional,
} from "../../../interfaces/schema";
import { dateToyyyyMMdd } from "../../../util/date";
import { getPatchOperations } from "../../../util/form";
import { klientkontoerklaeringValidationSchema } from "../person/personKlienterklaeringFormConfig";

export const firmaKlientkontoerklaeringForm = (
  firmaId: string,
  klienterklaeringId?: string,
  kke?: FirmaKlienterklaering,
): FormConfig<FirmaKlientkontoerklaeringDTO> => {
  const isLocked = kke?.erklModtaget ? true : false;
  return {
    title: "klientkontoerklaering",
    lockedValues: [
      {
        id: "firmaId",
        value: firmaId,
      },
      {
        id: "klienterklaeringId",
        value: klienterklaeringId,
      },
    ],
    validationSchema: klientkontoerklaeringValidationSchema,
    form: [
      {
        component: "textinput",
        id: "erklAar",
        label: "Erklæringsår",
        locked: true,
        initialValue: "",
        isRequired: true,
      },
      {
        component: "kode-select",
        kodeListeType: "KlienterklaeringstypeKode",
        id: "klienterklaeringstype",
        label: "Type",
        placeholder: "Vælg type",
        locked: true,
        initialValue: "",
        isRequired: true,
      },
      {
        component: "textinput",
        id: "firmaCvrNr",
        label: "CVR firma",
        locked: true,
        initialValue: "",
      },
      {
        component: "kode-select",
        kodeListeType: "KlienterklaeringStatusKode",
        id: "klienterklaeringStatus",
        label: "Status",
        placeholder: "Vælg status",
        locked: false,
        initialValue: "",
        isRequired: true,
        filter: isLocked ? ["FristOrdinaer"] : undefined,
      },
      {
        component: "custom",
        id: "indberettetformular",
        label: "Formular",
        initialValue: "Formular1",
        locked: isLocked,
        isRequired: true,
        conditionalHidden: ({ values }) =>
          values.klienterklaeringStatus === "FristOrdinaer" ||
          values.klienterklaeringStatus === "",
        customComponent: ({ formik }) => (
          <AutoCompleteFormular
            onSelectedItemEvent={(item) =>
              formik.setFieldValue(
                "indberettetformular",
                item.selectedItem?.id,
                true,
              )
            }
            isLocked={isLocked}
            initialValue={formik.values.indberettetformular}
            label="Formular"
            formik={formik}
            id="indberettetformular"
            isRequired
          />
        ),
      },
      {
        component: "datepicker",
        id: "erklModtaget",
        label: "Modtaget dato",
        locked: isLocked,
        initialValue: "",
        isRequired: true,
        conditionalHidden: ({ values }) =>
          values.klienterklaeringStatus === "FristOrdinaer" ||
          values.klienterklaeringStatus === "",
      },
      {
        component: "textinput",
        id: "erklNr",
        label: "Nr. analog erklæring",
        locked: isLocked,
        initialValue: "",
        conditionalHidden: ({ values }) =>
          values.klienterklaeringStatus === "FristOrdinaer" ||
          values.klienterklaeringStatus === "",
      },
      {
        component: "datepicker",
        id: "periodeFraDato",
        label: "Startdato erklæringsperiode",
        locked: true,
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "periodeTilDatoInklusiv",
        label: "Slutdato erklæringsperiode",
        locked: true,
        initialValue: "",
        isRequired: true,
      },
      {
        component: "datepicker",
        id: "frist",
        label: "Frist",
        locked: true,
        isRequired: true,
        initialValue: "",
      },
    ],
    get: `firma/${firmaId}/klienterklaeringer/${klienterklaeringId}`,
    getDocument: `firma/${firmaId}/klienterklaeringer/${klienterklaeringId}/dokument`,
    downloadLink: `firma/${firmaId}/klienterklaeringer/${klienterklaeringId}/download`,
    patch(values: FirmaKlientkontoerklaeringDTO) {
      const isFristOrdinaer = values.klienterklaeringStatus === "FristOrdinaer";
      if (!isLocked && !isFristOrdinaer && !values.indberettetformular) {
        values.indberettetformular = "Formular1";
      }
      if (!values.klienterklaeringStatus) {
        delete (
          values as MakeOptional<PersonKlienterklaeringDTO, "erklModtaget">
        ).erklModtaget;
        delete (
          values as MakeOptional<
            PersonKlienterklaeringDTO,
            "indberettetformular"
          >
        ).indberettetformular;
        delete (values as MakeOptional<PersonKlienterklaeringDTO, "erklNr">)
          .erklNr;
      }
      return {
        query: `firma/${firmaId}/klienterklaeringer/${klienterklaeringId}`,
        body: JSON.stringify(getPatchOperations(values)),
      };
    },
    post(values: FirmaKlientkontoerklaeringDTO) {
      const {
        erklAar,
        erklModtaget,
        erklNr,
        firmaCvrNr,
        klienterklaeringStatus,
        klienterklaeringstype,
        periodeFraDato,
        periodeTilDatoInklusiv,
        indberettetformular,
        frist,
      } = values;
      const isFristOrdinaer = klienterklaeringStatus === "FristOrdinaer";
      return {
        query: `firma/${firmaId}/klienterklaeringer`,
        body: {
          erklAar: erklAar ? erklAar.toString() : null,
          erklModtaget:
            erklModtaget && !isFristOrdinaer
              ? dateToyyyyMMdd(erklModtaget)
              : null,
          erklNr: erklNr ? erklNr : null,
          firmaCvrNr: firmaCvrNr ? firmaCvrNr : null,
          klienterklaeringStatus: klienterklaeringStatus.toString(),
          klienterklaeringstype: klienterklaeringstype.toString(),
          periodeFraDato: dateToyyyyMMdd(periodeFraDato),
          periodeTilDatoInklusiv: dateToyyyyMMdd(periodeTilDatoInklusiv),
          indberettetformular: isFristOrdinaer
            ? null
            : indberettetformular
              ? "Formular1"
              : "Formular2",
          frist: dateToyyyyMMdd(frist),
        },
      };
    },
    delete() {
      return {
        query: `firma/${firmaId}/klienterklaeringer/${klienterklaeringId}`,
      };
    },
  };
};
